import { useCallback, useRef, useState, useEffect } from 'react';

/**
 * Per-user, per-board hidden-column storage.
 *
 * Returns
 *   hidden       : Set<string> of column keys that are currently hidden
 *   isHidden(k)  : convenience boolean check
 *   toggle(k)    : flip a column's visibility (and persist)
 *   reset()      : show everything again
 *
 * Side effect: maintains a single `<style>` element in <head> that hides
 * each `class="col-{key}"` cell whose key is in the set. Direct DOM access
 * is used rather than a React-rendered `<style>` because some browser-side
 * scripts (analytics / translation extensions) wrap the text content of
 * React-rendered <style> tags in <span>s, which silently invalidates the
 * stylesheet (sheet.cssRules becomes empty).
 *
 * Storage is in localStorage (not sessionStorage) so a PM's preferences
 * survive a tab close. Keyed by user email so two people sharing a browser
 * don't clobber each other. The first render typically happens before the
 * auth context has populated `userEmail`, so we use an anon key; once the
 * email arrives the key changes and we re-read from storage. Crucially we
 * only persist back on *user-initiated* toggles, so the key change doesn't
 * silently clobber the per-user record with the anon-render's empty state.
 */
export function useHiddenColumns(board, userEmail) {
  const key = `restorepm:columns-hidden:${userEmail || 'anon'}:${board}`;

  const readForKey = (k) => {
    try {
      const raw = localStorage.getItem(k);
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* ignore */ }
    return new Set();
  };

  const [hidden, setHidden] = useState(() => readForKey(key));

  // Re-read whenever the key changes (e.g. user just logged in / email
  // resolved). Do NOT persist here — that would write the stale state into
  // the new key before the read lands.
  useEffect(() => {
    setHidden(readForKey(key));
  }, [key]);

  // Persist only after explicit user actions (toggle / reset). Tracking via
  // a ref means re-renders caused by key changes never trigger a write.
  const userActionRef = useRef(false);
  useEffect(() => {
    if (!userActionRef.current) return;
    userActionRef.current = false;
    try { localStorage.setItem(key, JSON.stringify(Array.from(hidden))); }
    catch { /* ignore */ }
  }, [key, hidden]);

  // Maintain a single <style> element in <head> for this board.
  useEffect(() => {
    const styleId = `restorepm-hidden-cols-${board}`;
    let el = document.getElementById(styleId);
    if (!el) {
      el = document.createElement('style');
      el.id = styleId;
      el.setAttribute('data-board', board);
      document.head.appendChild(el);
    }
    el.textContent = hidden.size === 0
      ? ''
      : Array.from(hidden).map((k) => `.col-${k} { display: none !important; }`).join('\n');
    return () => {
      const cur = document.getElementById(styleId);
      if (cur) cur.remove();
    };
  }, [board, hidden]);

  const toggle = useCallback((columnKey) => {
    userActionRef.current = true;
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(columnKey)) next.delete(columnKey); else next.add(columnKey);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    userActionRef.current = true;
    setHidden(new Set());
  }, []);

  const isHidden = useCallback((columnKey) => hidden.has(columnKey), [hidden]);

  return { hidden, isHidden, toggle, reset, styleTag: null };
}

export default useHiddenColumns;


